import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gem, Heart, Lock, LogOut, Package, RotateCcw, User } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { api, eur } from "../lib/api";
import { DatePickerField } from "../components/DatePickerField";

const TOKEN_KEY = "ap-cust-token";

export default function Account() {
  const { t, localize } = useLanguage();
  const a = t.acc;
  const a2 = t.acc2;
  const a3 = t.acc3;
  const { mergeFromServer, slugs: wishSlugs } = useWishlist();
  const { addItem, setOpen } = useCart();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [me, setMe] = useState(null);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [profile, setProfile] = useState({ name: "", birthday: "" });
  const [savedMsg, setSavedMsg] = useState("");
  const [products, setProducts] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [redeemMsg, setRedeemMsg] = useState("");
  const [cancelMsg, setCancelMsg] = useState({});

  useEffect(() => {
    api.products().then(setProducts).catch(() => {});
  }, []);

  useEffect(() => {
    if (!token) return;
    api.accountMe(token)
      .then((d) => {
        setMe(d);
        setProfile({ name: d.name || "", birthday: d.birthday || "" });
        mergeFromServer(d.wishlist || []);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      });
    api.accountLoyalty(token).then(setLoyalty).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const { lang } = useLanguage();

  const forgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setInfoMsg("");
    try {
      await api.accountForgot({ email: form.email, lang });
      setInfoMsg(a2.forgotOk);
    } catch {
      setInfoMsg(a2.forgotOk);
    }
  };

  const redeem = async (points) => {
    setRedeemMsg("");
    try {
      const res = await api.accountRedeem(token, points);
      setRedeemMsg(`${a2.redeemDone}: ${res.code}`);
      setLoyalty(await api.accountLoyalty(token));
    } catch (err) {
      setRedeemMsg(err.response?.status === 400 ? a2.notEnough : t.common.error);
    }
  };

  const reorder = (order) => {
    order.items.forEach((i) => {
      const p = products.find((x) => x.slug === i.slug);
      if (p) addItem(p, i.qty, i.variant || 60);
    });
    setOpen(true);
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm(a3.cancelConfirm)) return;
    try {
      const res = await api.accountCancelOrder(token, orderId);
      setCancelMsg((prev) => ({ ...prev, [orderId]: res.refunded ? a3.cancelDone : a3.cancelDoneNoRefund }));
      const d = await api.accountMe(token);
      setMe(d);
      setLoyalty(await api.accountLoyalty(token));
    } catch {
      setCancelMsg((prev) => ({ ...prev, [orderId]: a3.cancelErr }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res =
        mode === "login"
          ? await api.accountLogin({ email: form.email, password: form.password })
          : await api.accountRegister(form);
      localStorage.setItem(TOKEN_KEY, res.token);
      setToken(res.token);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (mode === "register" && form.password.length < 8) setError(a.errPwd);
      else if (status === 400) setError(detail === "Email already registered" ? a.errExists : detail || t.common.error);
      else if (status === 401) setError(a.errInvalid);
      else setError(typeof detail === "string" ? detail : t.common.error);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setMe(null);
  };

  const saveProfile = async () => {
    const d = await api.accountUpdate(token, { name: profile.name, birthday: profile.birthday });
    setMe((prev) => ({ ...prev, name: d.name, birthday: d.birthday }));
    setSavedMsg(a.saved);
    setTimeout(() => setSavedMsg(""), 2500);
  };

  const inputCls =
    "w-full border border-navy/15 bg-transparent px-4 py-3 text-sm text-navy outline-none transition-colors focus:border-gold placeholder:text-navy/35";

  if (!token || !me) {
    return (
      <div data-testid="account-auth-page" className="noise-bg flex min-h-screen items-center justify-center bg-navy px-6 pt-32 pb-24">
        <div className="w-full max-w-md">
          <div className="border border-white/10 bg-white/5 p-10 backdrop-blur-sm">
            <User size={28} strokeWidth={1.25} className="text-gold" />
            <h1 className="mt-6 font-display text-3xl font-light text-white">{a.title}</h1>
            {mode !== "forgot" && (
              <div className="mt-8 flex border border-white/15">
                {["login", "register"].map((m) => (
                  <button
                    key={m}
                    data-testid={`account-mode-${m}`}
                    onClick={() => { setMode(m); setError(""); setInfoMsg(""); }}
                    className={`flex-1 px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] transition-colors ${
                      mode === m ? "bg-gold text-navy" : "text-white/60 hover:text-gold"
                    }`}
                  >
                    {m === "login" ? a.loginTab : a.registerTab}
                  </button>
                ))}
              </div>
            )}
            {mode === "forgot" ? (
              <form onSubmit={forgotPassword} data-testid="account-forgot-form" className="mt-8 space-y-5">
                <input
                  data-testid="account-forgot-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email"
                  required
                  className={`${inputCls} !border-white/15 !text-white placeholder:!text-white/35`}
                />
                {infoMsg && <p data-testid="account-forgot-ok" className="text-xs text-gold">{infoMsg}</p>}
                <button type="submit" data-testid="account-forgot-submit" className="w-full bg-gold py-3.5 text-[11px] font-bold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-gold-dark">
                  {a2.forgotBtn}
                </button>
                <button type="button" data-testid="account-back-login" onClick={() => { setMode("login"); setInfoMsg(""); }} className="w-full text-center text-[10px] font-semibold uppercase tracking-[0.14em] text-white/50 transition-colors hover:text-gold">
                  {a2.backToLogin}
                </button>
              </form>
            ) : (
            <form onSubmit={submit} data-testid="account-auth-form" className="mt-6 space-y-5">
              {mode === "register" && (
                <input
                  data-testid="account-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={a.name}
                  required
                  className={`${inputCls} !border-white/15 !text-white placeholder:!text-white/35`}
                />
              )}
              <input
                data-testid="account-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                required
                className={`${inputCls} !border-white/15 !text-white placeholder:!text-white/35`}
              />
              <input
                data-testid="account-password-input"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={a.password}
                required
                className={`${inputCls} !border-white/15 !text-white placeholder:!text-white/35`}
              />
              {error && <p data-testid="account-auth-error" className="text-xs text-red-300">{error}</p>}
              <button type="submit" data-testid="account-submit-button" className="w-full bg-gold py-3.5 text-[11px] font-bold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-gold-dark">
                {mode === "login" ? a.loginTab : a.registerTab}
              </button>
              {mode === "login" && (
                <button type="button" data-testid="account-forgot-link" onClick={() => { setMode("forgot"); setError(""); }} className="w-full text-center text-[10px] font-semibold uppercase tracking-[0.14em] text-white/50 transition-colors hover:text-gold">
                  {a2.forgot}
                </button>
              )}
            </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="account-dashboard" className="min-h-screen bg-surface pt-24">
      <div className="mx-auto max-w-5xl px-6 pb-24">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-4xl font-light text-navy">{a.title}</h1>
            <p className="mt-2 text-sm text-navy/50">{a.welcome}, {me.name}</p>
          </div>
          <button onClick={logout} data-testid="account-logout-button" className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/50 transition-colors hover:text-red-600">
            <LogOut size={14} /> {a.logout}
          </button>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <section data-testid="account-orders" className="border border-navy/10 bg-white lg:col-span-2">
            <h2 className="label-luxe flex items-center gap-2 border-b border-navy/10 px-6 py-4 text-navy">
              <Package size={14} className="text-gold-dark" /> {a.orders}
            </h2>
            {me.orders.length === 0 ? (
              <p className="p-8 text-sm text-navy/40">{a.noOrders}</p>
            ) : (
              me.orders.map((o) => (
                <div key={o.id} data-testid={`account-order-${o.id}`} className="flex flex-wrap items-center justify-between gap-3 border-b border-navy/5 px-6 py-4 last:border-0">
                  <div>
                    <p className="font-mono text-xs font-semibold text-gold-dark">{o.id}</p>
                    <p className="mt-1 text-xs text-navy/50">{new Date(o.created_at).toLocaleDateString()} · {o.items.map((i) => `${i.name} ×${i.qty}`).join(", ")}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    {o.status !== "cancelled" && !["shipped", "delivered"].includes(o.fulfillment) && (
                      <button
                        onClick={() => cancelOrder(o.id)}
                        data-testid={`account-cancel-${o.id}`}
                        className="text-[10px] font-semibold uppercase tracking-[0.14em] text-red-500/80 transition-colors hover:text-red-600"
                      >
                        {a3.cancel}
                      </button>
                    )}
                    <button
                      onClick={() => reorder(o)}
                      data-testid={`account-reorder-${o.id}`}
                      className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-gold-dark transition-colors hover:text-navy"
                    >
                      <RotateCcw size={11} /> {a2.reorder}
                    </button>
                    <span className={`border px-2 py-1 text-[9px] font-bold uppercase tracking-[0.12em] ${
                      o.status === "cancelled"
                        ? "border-red-300 bg-red-50 text-red-600"
                        : "border-sage/50 bg-sage-light/60 text-sage-dark"
                    }`} data-testid={`account-order-status-${o.id}`}>
                      {o.status === "cancelled" ? a3.cancelled : (o.fulfillment || o.status)}
                    </span>
                    <span className="text-sm font-semibold text-navy">{eur(o.total)}</span>
                  </div>
                  {cancelMsg[o.id] && (
                    <p data-testid={`account-cancel-msg-${o.id}`} className="mt-2 w-full text-xs font-semibold text-gold-dark">{cancelMsg[o.id]}</p>
                  )}
                </div>
              ))
            )}
          </section>

          <section data-testid="account-loyalty" className="border border-gold/30 bg-white">
            <h2 className="label-luxe flex items-center gap-2 border-b border-navy/10 px-6 py-4 text-navy">
              <Gem size={14} className="text-gold-dark" /> {a2.loyalty}
            </h2>
            {loyalty && (
              <div className="p-6">
                <div className="flex items-center gap-3" data-testid="loyalty-level">
                  <span className={`border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] ${
                    loyalty.level.name === "Platinum"
                      ? "border-gold bg-navy text-gold"
                      : loyalty.level.name === "Gold"
                        ? "border-gold/50 bg-gold/15 text-gold-dark"
                        : "border-navy/20 text-navy/60"
                  }`}>
                    {loyalty.level.name}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.14em] text-navy/45">×{loyalty.level.multiplier} {a3.multiplier}</span>
                </div>
                {loyalty.nextLevel ? (
                  <div className="mt-3" data-testid="loyalty-progress">
                    <p className="text-xs text-navy/50">{eur(loyalty.nextLevel.remaining)} {a3.toNext} ({loyalty.nextLevel.name})</p>
                    <div className="mt-2 h-1 w-full bg-navy/10">
                      <div
                        className="h-1 bg-gold transition-all"
                        style={{
                          width: `${Math.min(100, Math.max(0, Math.round(((loyalty.totalSpent - loyalty.level.min) / (loyalty.nextLevel.min - loyalty.level.min)) * 100)))}%`,
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="mt-3 text-xs font-semibold text-gold-dark" data-testid="loyalty-progress">{a3.topLevel}</p>
                )}
                <p className="mt-4 font-display text-4xl font-light text-navy" data-testid="loyalty-balance">
                  {loyalty.balance} <span className="text-sm text-navy/40">{a2.points}</span>
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {loyalty.tiers.map((tier) => (
                    <button
                      key={tier.points}
                      onClick={() => redeem(tier.points)}
                      disabled={loyalty.balance < tier.points}
                      data-testid={`redeem-${tier.points}`}
                      className="btn-outline-luxe !px-4 !py-2 disabled:opacity-40"
                    >
                      {a2.redeemFor} {tier.points} → {eur(tier.amount)}
                    </button>
                  ))}
                </div>
                {redeemMsg && <p data-testid="redeem-msg" className="mt-3 text-xs font-semibold text-gold-dark">{redeemMsg}</p>}
                {loyalty.history.length > 0 && (
                  <div className="mt-5 border-t border-navy/10 pt-4">
                    <p className="label-luxe mb-3 text-navy/50">{a2.history}</p>
                    {loyalty.history.map((h) => (
                      <div key={h.code} data-testid={`loyalty-coupon-${h.code}`} className="flex items-center justify-between py-1.5 text-xs">
                        <span className="font-mono font-semibold text-gold-dark">{h.code}</span>
                        <span className="text-navy/50">{eur(h.amount)} · {new Date(h.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>

          <section data-testid="account-wishlist" className="border border-navy/10 bg-white">
            <h2 className="label-luxe flex items-center gap-2 border-b border-navy/10 px-6 py-4 text-navy">
              <Heart size={14} className="text-gold-dark" /> {a.wishlist}
            </h2>
            {wishSlugs.length === 0 ? (
              <p className="p-8 text-sm text-navy/40">{a.noWish}</p>
            ) : (
              wishSlugs.map((slug) => {
                const p = products.find((x) => x.slug === slug);
                return (
                  <Link
                    key={slug}
                    to={`/product/${slug}`}
                    data-testid={`account-wish-${slug}`}
                    className="flex items-center justify-between border-b border-navy/5 px-6 py-4 text-sm text-navy transition-colors last:border-0 hover:bg-surface"
                  >
                    <span>{p ? localize(p.i18n).name || slug : slug}</span>
                    <span className="text-xs font-semibold text-gold-dark">{p ? eur(p.price ?? 0) : ""}</span>
                  </Link>
                );
              })
            )}
          </section>

          <section data-testid="account-profile" className="border border-navy/10 bg-white p-6">
            <h2 className="label-luxe flex items-center gap-2 text-navy">
              <Lock size={14} className="text-gold-dark" /> {a.profile}
            </h2>
            <div className="mt-6 space-y-4">
              <input
                data-testid="account-profile-name"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                placeholder={a.name}
                className={inputCls}
              />
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/50">{a.birthday}</label>
                <DatePickerField
                  value={profile.birthday}
                  onChange={(v) => setProfile({ ...profile, birthday: v })}
                  testid="account-birthday-input"
                />
              </div>
              <p className="text-xs text-navy/40">{a.birthdayNote}</p>
              <div className="flex items-center gap-3">
                <button onClick={saveProfile} data-testid="account-profile-save" className="btn-outline-luxe !px-5 !py-2.5">
                  {a.save}
                </button>
                {savedMsg && <span data-testid="account-profile-saved" className="text-xs font-semibold text-gold-dark">{savedMsg}</span>}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
